import { FormEvent, useState, useEffect } from 'react';
import { Modal, ModalContent } from 'base/src/components/Modal';
import { Input } from 'base/src/components/Input';
import { Button } from 'base/src/components/Button';
import { useTheme } from 'base/src/utils/context/themeContext';

import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { EnviarEmailRecuperacao, LimparEstadoRecuperarSenha } from '../../../../store/recuperarSenha/actions/recuperarSenhaAction';
import { RecuperarSenhaRequest } from '../../../../types/recuperarSenha/recuperarSenhaRequest';
import * as S from './styles';
import { RootStateAuth } from '../../../../store/authReducer';

export interface RecuperarSenhaProps {
  isOpen: boolean;
  onClose: () => void;
}

const connector = connectUtil(
  (state: RootStateAuth) => ({
    validationResult: state.RecuperarSenhaReducer.validationResult
  }),
  { EnviarEmailRecuperacao, LimparEstadoRecuperarSenha }
);

type Props = RecuperarSenhaProps & PropsFromRedux<typeof connector>;

function RecuperarSenha({
  isOpen,
  onClose,
  validationResult,
  EnviarEmailRecuperacao,
  LimparEstadoRecuperarSenha
}: Props): JSX.Element {
  const { theme } = useTheme();

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);

  // Reset form and state when modal is closed
  useEffect(() => {
    if (!isOpen && validationResult) {
      LimparEstadoRecuperarSenha();
    }
  }, [isOpen, validationResult, LimparEstadoRecuperarSenha]);

  function validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  function handleEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(value === '' || validateEmail(value));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    const request: RecuperarSenhaRequest = {
      Email: email
    };

    EnviarEmailRecuperacao(request);
  }

  function handleClose() {
    setEmail('');
    setIsValidEmail(true);
    onClose();
  }

  const hasValidResult = validationResult !== null;
  const emailFoiEnviado = hasValidResult && validationResult.IsValid;
  const modalTitle = emailFoiEnviado ? "Email Enviado" : hasValidResult ? "Erro no Envio" : "Recuperar Senha";

  return (
    <Modal open={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        <S.ModalTitle>
          {modalTitle}
        </S.ModalTitle>

        {!hasValidResult || !validationResult.IsValid && !validationResult.Message ? (
          <S.FormContainer onSubmit={handleSubmit}>
            <S.Descricao>
              Digite seu email cadastrado para receber as instruções de recuperação de senha.
            </S.Descricao>

            <Input
              borderRadius='50px'
              form={false}
              name='email'
              label='Email'
              placeholder='Seu email'
              height='40px'
              borderColor={isValidEmail ? theme.colors.grey_text : theme.colors.negative}
              value={email}
              onChange={handleEmailChange}
            />

            <S.ButtonContainer>
              <Button
                style={{ borderRadius: 100 }}
                type='submit'
                size='lg'
                variant='primary'
                full={true}
                disabled={!email || !isValidEmail}
              >
                Enviar
              </Button>
            </S.ButtonContainer>
          </S.FormContainer>
        ) : (
          <S.SuccessContainer>
            <S.SuccessMessage isError={!validationResult.IsValid}>
              <p>{validationResult.Message}</p>
            </S.SuccessMessage>

            <Button
              style={{ borderRadius: 100 }}
              onClick={handleClose}
              size='lg'
              variant='primary'
              full={true}
            >
              Fechar
            </Button>
          </S.SuccessContainer>
        )}
      </ModalContent>
    </Modal>
  );
}

export default connector(RecuperarSenha);